import { useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { SEO } from "components/SEO";

const API_URL =
  "https://us-central1-efektivos-dev-a03c3.cloudfunctions.net/api/v1/customers/delete-account-request";

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Inicio",
      item: "https://efektivos.com/",
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Eliminar Cuenta",
      item: "https://efektivos.com/delete-account",
    },
  ],
};

const STEPS = [
  "Recibiremos tu solicitud de eliminación.",
  "Nuestro equipo verificará la información proporcionada.",
  "Procesaremos la eliminación de tu cuenta y de los datos asociados cuando corresponda.",
  "Algunas transacciones o registros podrán conservarse cuando exista una obligación legal o regulatoria.",
];

export const DeleteAccount = () => {
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 8);
    return digits.length > 4 ? `${digits.slice(0, 4)}-${digits.slice(4)}` : digits;
  };

  const rawDigits = phone.replace("-", "");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const body = { phone: `+502${rawDigits}` };
      if (email.trim()) body.email = email.trim();

      const { data } = await axios.post(API_URL, body);

      if (data.status) {
        Swal.fire({
          title: "Solicitud enviada",
          text: "Tu solicitud de eliminación ha sido recibida. Nuestro equipo la procesará en los próximos días hábiles.",
          icon: "success",
          confirmButtonColor: "#84cc16",
        }).then(() => navigate("/"));
      } else {
        Swal.fire({
          title: "Número no encontrado",
          text: "El número de teléfono ingresado no está asociado a ninguna cuenta.",
          icon: "error",
          confirmButtonColor: "#84cc16",
        });
        setPhone("");
      }
    } catch {
      Swal.fire({
        title: "Error",
        text: "Ocurrió un problema al enviar tu solicitud. Intenta de nuevo más tarde.",
        icon: "error",
        confirmButtonColor: "#84cc16",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Solicitar Eliminación de Cuenta"
        description="Solicita la eliminación de tu cuenta y datos personales de Efektivos. Ingresa tu número de teléfono para iniciar el proceso."
        path="/delete-account"
        structuredData={breadcrumbSchema}
        noindex={true}
      />

      <main className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-xl mx-auto">

          {/* Header */}
          <div className="text-center mb-8">
            <img
              src={`${process.env.PUBLIC_URL}/assets/efektivos_logo.png`}
              alt="Efektivos"
              className="h-20 mx-auto mb-5"
            />
            <h1 className="text-3xl font-bold text-gray-900">
              Solicitar eliminación de cuenta
            </h1>
          </div>

          {/* Info card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
            <p className="text-gray-600 mb-3">
              Puedes utilizar este formulario para solicitar la eliminación de
              tu cuenta de Efektivos y de los datos personales asociados.
            </p>
            <p className="text-gray-600">
              Nuestro equipo revisará y procesará tu solicitud.{" "}
              <strong>La eliminación no ocurre de forma automática</strong> al
              enviar este formulario.
            </p>
          </div>

          {/* Form card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
            <form onSubmit={handleSubmit} noValidate>
              {/* Phone */}
              <div className="mb-4">
                <label
                  htmlFor="phone"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Número de teléfono asociado a tu cuenta
                </label>
                <div className="flex items-center border-2 border-gray-200 rounded-xl overflow-hidden focus-within:border-lime-500 transition-colors bg-white">
                  <span className="px-4 py-3 text-gray-500 font-medium bg-gray-50 border-r border-gray-200 text-sm select-none">
                    +502
                  </span>
                  <input
                    id="phone"
                    type="text"
                    inputMode="numeric"
                    className="flex-1 px-4 py-3 text-gray-900 outline-none text-lg placeholder-gray-400"
                    placeholder="0000-0000"
                    value={phone}
                    onChange={(e) => setPhone(formatPhone(e.target.value))}
                    maxLength={9}
                    required
                  />
                </div>
              </div>

              {/* Email (optional) */}
              <div className="mb-6">
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Correo electrónico{" "}
                  <span className="font-normal text-gray-400">(opcional)</span>
                </label>
                <input
                  id="email"
                  type="email"
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-gray-900 outline-none focus:border-lime-500 transition-colors placeholder-gray-400"
                  placeholder="tu@correo.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || rawDigits.length < 8}
                className="w-full bg-lime-500 hover:bg-lime-600 disabled:bg-gray-200 disabled:text-gray-400 text-white font-bold py-3 px-6 rounded-xl text-base transition-colors flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8H4z"
                      />
                    </svg>
                    Procesando...
                  </>
                ) : (
                  "Solicitar eliminación"
                )}
              </button>
            </form>
          </div>

          {/* What happens next */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              ¿Qué sucede después?
            </h2>
            <ol className="space-y-3">
              {STEPS.map((step, i) => (
                <li key={i} className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-lime-100 text-lime-700 text-sm font-bold flex items-center justify-center mt-0.5">
                    {i + 1}
                  </span>
                  <p className="text-gray-600 text-sm">{step}</p>
                </li>
              ))}
            </ol>
            <div className="mt-5 pt-4 border-t border-gray-100">
              <p className="text-sm text-gray-500">
                <span className="font-semibold text-gray-700">
                  Tiempo estimado de procesamiento:
                </span>{" "}
                hasta 10 días hábiles.
              </p>
            </div>
          </div>

          {/* Help */}
          <div className="bg-lime-50 border border-lime-100 rounded-2xl p-6">
            <h2 className="text-base font-bold text-gray-900 mb-2">
              ¿Necesitas ayuda?
            </h2>
            <p className="text-sm text-gray-600 mb-3">
              Si tienes problemas usando este formulario o necesitas asistencia
              adicional, contáctanos en:
            </p>
            <a
              href="mailto:soporte@efektivos.com"
              className="inline-flex items-center gap-2 text-lime-700 font-semibold text-sm hover:text-lime-900 transition-colors"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              soporte@efektivos.com
            </a>
          </div>

        </div>
      </main>
    </>
  );
};
